import { Button, Col, Input, Space, Table, TablePaginationConfig } from "antd";
import { FilterValue, SorterResult } from "antd/lib/table/interface";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RootState } from "../../../app/store";
import { getPaginatedProvidersAsync } from "../../../features/driver/driverSlice";
import { WebRoutes } from "../../../shared/constants/WebRoutes";
import { DataType, driverColumns } from "./columns";
import { getProvidersExcel } from "../../../services/adminProviderApi";
import ContainerLayout from "../../common/containerLayout";
import { PlusCircleTwoTone } from "@ant-design/icons";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";

import "../../../shared/styles/list.css";

const { Search } = Input;

export interface TableParams {
  pagination?: TablePaginationConfig;
  sortField?: string;
  sortOrder?: string;
  filters: Record<string, FilterValue | null>;
}

const DriverList = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { data, loading } = useAppSelector((state: RootState) => state.drivers);

  const [search, setSearch] = useState("");
  const [ignoreFetch, setIgnoreFetch] = useState(false);
  const [isLoadingExcel, setIsLoadingExcel] = useState(false);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableParams, setTableParams] = useState<TableParams>({
    filters: {
      active: [true],
    },
    pagination: {
      current: 1,
      pageSize: 10,
    },
  });

  const fetchData = (filters?: string) => {
    dispatch(
      getPaginatedProvidersAsync({
        page: tableParams.pagination?.current || 1,
        take: tableParams.pagination?.pageSize || 10,
        filters,
      }),
    ).then(({ payload }: any) => {
      setTableParams({
        ...tableParams,
        pagination: {
          ...tableParams.pagination,
          total: payload.total,
        },
      });
    });

    setIgnoreFetch(true);
  };

  useEffect(() => {
    if (!ignoreFetch) {
      let filters;

      if (search) {
        filters = JSON.stringify({
          first_name: search,
          last_name: search,
        });
      }

      fetchData(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(tableParams), search]);

  const handleTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<DataType> | SorterResult<DataType>[],
  ) => {
    setIgnoreFetch(false);
    setTableParams({
      pagination,
      filters,
      ...sorter,
    });
  };

  const onSearch = (value: string) => {
    setIgnoreFetch(false);
    setSearch(value);
    setTableParams({
      ...tableParams,
      pagination: {
        ...tableParams.pagination,
        current: 1,
      },
    });
  };

  const handleExcelExport = async () => {
    setIsLoadingExcel(true);
    const result = await getProvidersExcel();
    setIsLoadingExcel(false);
    const link = document.createElement("a");
    link.href = result;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <ContainerLayout showNotification={false} title="Conductores">
      <Col span={24}>
        <Space>
          <Button
            type="primary"
            icon={<PlusCircleTwoTone />}
            onClick={() =>
              navigate(WebRoutes.CreateDriverRoute, {
                replace: true,
              })
            }
          >
            Conductor
          </Button>
          <Search
            placeholder="Nombre del conductor"
            allowClear
            onSearch={onSearch}
            loading={loading}
            disabled={loading}
          />
          <Button
            onClick={() =>
              navigate(WebRoutes.ProviderDocumentListRoute, {
                replace: true,
              })
            }
          >
            Lista de liquidaciones
          </Button>
          <Button loading={isLoadingExcel} onClick={() => handleExcelExport()}>
            Descargar lista
          </Button>
        </Space>
      </Col>
      <Col
        ref={tableContainerRef}
        span={24}
        style={{ width: "100px", height: "83vh", position: "relative" }}
      >
        <Table
          scroll={{ y: "calc(-250px + 91vh)" }}
          style={{ minWidth: "100%" }}
          columns={driverColumns(navigate, tableParams, search, tableContainerRef)}
          rowKey={(record) => record.id}
          dataSource={data}
          pagination={tableParams.pagination}
          loading={loading}
          onChange={handleTableChange}
        />
      </Col>
    </ContainerLayout>
  );
};

export default DriverList;
